import React, { useState, useEffect } from 'react';
import { X, Gift, Zap, ArrowRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

// Carte d'annonce NON-BLOQUANTE (coin bas-gauche). Ne recouvre jamais le hero.
// Dismiss persistant 7 jours via localStorage.
const DISMISS_KEY = 'emsAnnouncementDismissedUntil';
const SHOW_DELAY_MS = 8000;
const DISMISS_DAYS = 7;

export const EMSAnnouncementPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const calc = () => {
      const opening = new Date('2026-09-28T00:00:00').getTime();
      const diff = opening - Date.now();
      if (diff > 0) {
        return {
          days: Math.floor(diff / 86400000),
          hours: Math.floor((diff / 3600000) % 24),
          minutes: Math.floor((diff / 60000) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        };
      }
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };
    setTimeLeft(calc());
    const timer = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') {
      setIsVisible(false);
      return;
    }
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() < dismissedUntil) return;

    const timer = setTimeout(() => setIsVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400000));
  };

  const handleVisitSite = () => {
    window.open('https://www.body-up-ems.ch', '_blank', 'noopener,noreferrer');
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed z-[70] left-3 right-3 bottom-3 sm:left-5 sm:right-auto sm:bottom-5 sm:w-[340px] animate-ems-slidein"
      data-testid="ems-announcement-card"
    >
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-900 via-black to-zinc-900 border border-white/10 shadow-2xl shadow-primary/20">
        <div className="h-1 bg-gradient-to-r from-yellow-400 via-primary to-pink-500" />

        <button
          onClick={handleClose}
          className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-all z-10"
          aria-label={language === 'fr' ? 'Fermer' : 'Close'}
          data-testid="ems-announcement-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 border border-green-500/40 text-[10px] font-black text-green-400 tracking-wider">
              {language === 'fr' ? '🎉 NOUVELLE SALLE' : '🎉 NEW STUDIO'}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-[10px] font-bold text-white tracking-wider">
              <Zap className="w-3 h-3 text-yellow-400" /> EMS · NYON
            </span>
          </div>

          <h3 className="text-sm font-bold text-white leading-tight">
            {language === 'fr' ? 'EMS Training en Groupe — Ouverture 28 sept. 2026' : 'Group EMS Training — Opening Sept 28, 2026'}
          </h3>
          <p className="text-[11px] text-zinc-400 mt-1">
            {language === 'fr' ? 'Extension de Body-Up Personal Training' : 'Extension of Body-Up Personal Training'}
          </p>

          <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-gradient-to-r from-yellow-400/90 to-yellow-500/90">
            <Gift className="w-4 h-4 text-black shrink-0" />
            <div>
              <p className="text-sm font-black text-black leading-none">320 CHF OFFERTS</p>
              <p className="text-[10px] text-black/70 font-semibold">
                {language === 'fr' ? '4 séances EMS · 50 places' : '4 EMS sessions · 50 spots'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-1.5 mt-3">
            {[
              { value: timeLeft.days, label: language === 'fr' ? 'J' : 'D' },
              { value: timeLeft.hours, label: 'H' },
              { value: timeLeft.minutes, label: 'M' },
              { value: timeLeft.seconds, label: 'S' },
            ].map((item, i) => (
              <div key={i} className="bg-zinc-800/80 rounded-md py-1.5 text-center border border-zinc-700/50">
                <div className="text-base font-bold text-white leading-none">{item.value}</div>
                <div className="text-[8px] text-zinc-400 uppercase tracking-wide mt-0.5">{item.label}</div>
              </div>
            ))}
          </div>

          <button
            onClick={handleVisitSite}
            className="group mt-3 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-primary hover:opacity-90 text-black font-bold py-2.5 rounded-lg transition-all text-sm"
            data-testid="ems-announcement-cta"
          >
            {language === 'fr' ? 'Découvrir la nouvelle salle' : 'Discover the new studio'}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes ems-slidein {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-ems-slidein { animation: ems-slidein 0.4s ease-out; }
      `}</style>
    </div>
  );
};
